import { useEffect, useState } from "react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, CheckCircle2, Loader2, Zap } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select } from "@/components/ui/select";
import { TitleCaseInput } from "@/components/ui/text-input";
import {
  routerCreateSchema, type RouterCreateInput, type RouterDetail, type TestConnectionResponse,
} from "./schemas";
import { useCreateRouter, useTestConnection, useUpdateRouter } from "./api";
import type { ApiError } from "@/lib/api";

const DEFAULTS: RouterCreateInput = {
  nombre: "",
  descripcion: "",
  ip_address: "",
  via_vpn: false,
  api_user: "byteisp",
  api_pass: "",
  api_port: 8728,
  api_ssl: false,
  snmp_community: "",
  snmp_port: 161,
  snmp_version: "2c",
  wan_interface: "",
  ubicacion: "",
  tags: [],
};

function toForm(r: RouterDetail): RouterCreateInput {
  return {
    nombre: r.nombre,
    descripcion: r.descripcion ?? "",
    ip_address: r.ip_address,
    via_vpn: r.via_vpn,
    api_user: r.api_user,
    api_pass: "", // no se devuelve, queda vacío
    api_port: r.api_port,
    api_ssl: r.api_ssl,
    snmp_community: r.snmp_community ?? "",
    snmp_port: r.snmp_port ?? "",
    snmp_version: r.snmp_version ?? "2c",
    wan_interface: r.wan_interface ?? "",
    ubicacion: r.ubicacion ?? "",
    tags: r.tags ?? [],
  };
}

function clean<T>(v: T, dropEmptyString = true): T {
  if (typeof v === "string") return (dropEmptyString && v.trim() === "" ? undefined : v.trim()) as T;
  if (Array.isArray(v)) return v.map((x) => clean(x, dropEmptyString)) as T;
  if (v && typeof v === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v)) {
      const c = clean(val, dropEmptyString);
      if (c !== undefined) out[k] = c;
    }
    return out as T;
  }
  return v;
}

export function RouterFormDialog({
  open, onOpenChange, router, onSaved,
}: { open: boolean; onOpenChange: (v: boolean) => void; router?: RouterDetail | null; onSaved?: (id: string) => void }) {
  const editMode = !!router;
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<TestConnectionResponse | null>(null);

  const { register, handleSubmit, control, reset, formState: { errors } } = useForm<RouterCreateInput>({
    resolver: zodResolver(routerCreateSchema),
    defaultValues: DEFAULTS,
  });

  useEffect(() => {
    if (open) { reset(router ? toForm(router) : DEFAULTS); setError(null); setTestResult(null); }
  }, [open, router, reset]);

  const createMut = useCreateRouter();
  const updateMut = useUpdateRouter(router?.id ?? "");
  const testMut = useTestConnection();

  const onSubmit: SubmitHandler<RouterCreateInput> = async (values) => {
    setError(null);
    try {
      // En edit mode, si api_pass está vacío, quitarlo del payload
      const payload = clean({ ...values }) as RouterCreateInput;
      if (editMode && !values.api_pass) {
        delete (payload as Partial<RouterCreateInput>).api_pass;
      }
      const saved = editMode
        ? await updateMut.mutateAsync(payload)
        : await createMut.mutateAsync(payload);
      onSaved?.(saved.id);
      onOpenChange(false);
    } catch (e) { setError((e as ApiError).message ?? "No se pudo guardar"); }
  };

  async function handleTest() {
    if (!router) return;
    setTestResult(null);
    try {
      const r = await testMut.mutateAsync(router.id);
      setTestResult(r);
    } catch (e) {
      setTestResult({
        success: false,
        latency_ms: null,
        identity: null,
        board_name: null,
        version: null,
        error: (e as ApiError).message ?? "Error al probar conexión",
      });
    }
  }

  const submitting = createMut.isPending || updateMut.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="lg">
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogHeader>
          <DialogTitle>{editMode ? "Editar router" : "Nuevo router"}</DialogTitle>
          <DialogDescription>MikroTik con API para gestión y provisioning.</DialogDescription>
        </DialogHeader>
        <DialogContent>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Nombre *</Label>
              <TitleCaseInput control={control} name="nombre" placeholder="RB-Centro" />
              {errors.nombre && <span className="text-xs text-destructive">{errors.nombre.message}</span>}
            </div>
            <div className="col-span-2">
              <Label>IP de gestión *</Label>
              <Input {...register("ip_address")} placeholder="10.0.0.1 — pública o VPN, el sistema la alcanza igual" />
              {errors.ip_address && <span className="text-xs text-destructive">{errors.ip_address.message}</span>}
            </div>
            <div className="col-span-2 mt-2 rounded-md border bg-muted/20 p-3">
              <div className="mb-2 text-[10px] font-semibold uppercase text-muted-foreground">Credenciales API</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Usuario API</Label>
                  <Input {...register("api_user")} />
                </div>
                <div>
                  <Label>{editMode ? "Password API (dejar vacío para no cambiar)" : "Password API *"}</Label>
                  <Input type="password" {...register("api_pass")} />
                  {errors.api_pass && <span className="text-xs text-destructive">{errors.api_pass.message}</span>}
                </div>
                <div>
                  <Label>Puerto</Label>
                  <Input type="number" {...register("api_port")} />
                </div>
                <div>
                  <Label>
                    <input type="checkbox" {...register("api_ssl")} className="mr-1.5 h-3 w-3 align-middle" />
                    SSL (api-ssl puerto 8729)
                  </Label>
                </div>
              </div>
            </div>
            <div className="col-span-2">
              <Label>Ubicación / comentarios</Label>
              <Textarea rows={2} {...register("ubicacion")} />
            </div>
            <div className="col-span-2">
              <Label>Descripción interna</Label>
              <Textarea rows={2} {...register("descripcion")} />
            </div>
          </div>

          {/* Test connection (solo en edit) */}
          {editMode && (
            <div className="mt-3 rounded-md border bg-muted/20 p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs font-medium">Probar conexión</div>
                  <div className="text-[10px] text-muted-foreground">Valida las credenciales llamando al API del MikroTik.</div>
                </div>
                <Button type="button" size="sm" variant="outline" onClick={handleTest} disabled={testMut.isPending}>
                  {testMut.isPending ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
                  {testMut.isPending ? "Probando…" : "Test connection"}
                </Button>
              </div>
              {testResult && (
                <div className={`mt-2 rounded-md border p-2 text-xs ${testResult.success ? "border-emerald-400/50 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/20 dark:text-emerald-300" : "border-destructive/50 bg-destructive/10 text-destructive"}`}>
                  {testResult.success ? (
                    <div className="flex items-start gap-2">
                      <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
                      <div>
                        <div className="font-medium">Conexión OK — {testResult.latency_ms} ms</div>
                        {testResult.identity && <div>Identity: <span className="font-mono">{testResult.identity}</span></div>}
                        {testResult.board_name && <div>Board: <span className="font-mono">{testResult.board_name}</span></div>}
                        {testResult.version && <div>RouterOS: <span className="font-mono">{testResult.version}</span></div>}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2">
                      <AlertCircle size={14} className="mt-0.5 shrink-0" />
                      <span>{testResult.error ?? "Fallo desconocido"}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="mt-3 flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
              <AlertCircle size={14} className="mt-0.5 shrink-0" /><span>{error}</span>
            </div>
          )}
        </DialogContent>
        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button type="submit" size="sm" disabled={submitting}>
            {submitting ? "Guardando…" : editMode ? "Guardar cambios" : "Crear router"}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
